import { apiClient, PaginatedResponse } from './api';

// Types pour les entreprises (tenants)
export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country: string;
  status: 'active' | 'suspended' | 'inactive';
  max_vehicles: number;
  max_drivers: number;
  max_missions_per_month: number;
  features: Record<string, boolean>;
  created_at: string;
  updated_at: string;
  stats: {
    vehicle_count: number;
    driver_count: number;
    total_missions: number;
    completed_missions: number;
  };
}

export interface CreateTenantData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  max_vehicles?: number;
  max_drivers?: number;
  max_missions_per_month?: number;
  features?: Record<string, boolean>;
  admin_email: string;
  admin_password: string;
  admin_first_name: string;
  admin_last_name: string;
}

export interface UpdateTenantData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  status?: 'active' | 'suspended' | 'inactive';
  max_vehicles?: number;
  max_drivers?: number;
  max_missions_per_month?: number;
  features?: Record<string, boolean>;
}

export interface TenantFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'suspended' | 'inactive';
}

export interface GlobalStats {
  total_tenants: number;
  active_tenants: number;
  total_vehicles: number;
  total_drivers: number;
  total_missions: number;
  completed_missions: number;
  missions_this_month: number;
}

export interface CompanySettings {
  company: {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    country: string;
    website?: string;
    timezone: string;
    language: string;
  };
  notifications: {
    email_enabled: boolean;
    sms_enabled: boolean;
    mission_alerts: boolean;
    maintenance_alerts: boolean;
    driver_alerts: boolean;
    system_alerts: boolean;
  };
  business: {
    working_hours: {
      start: string;
      end: string;
    };
    working_days: number[];
    currency: string;
    date_format: string;
    time_format: string;
  };
  features: Record<string, any>;
}

export interface UpdateCompanySettingsData {
  company?: Partial<CompanySettings['company']>;
  notifications?: Partial<CompanySettings['notifications']>;
  business?: Partial<CompanySettings['business']>;
  features?: Record<string, any>;
}

/**
 * Service pour la gestion des entreprises (Super Admin)
 */
export class TenantService {
  private readonly baseUrl = '/super-admin';

  /**
   * Récupérer la liste des entreprises avec filtres et pagination
   */
  async getTenants(filters: TenantFilters = {}): Promise<PaginatedResponse<Tenant>> {
    const url = apiClient.buildUrl(`${this.baseUrl}/tenants`, filters);
    return apiClient.get<PaginatedResponse<Tenant>>(url);
  }

  /**
   * Récupérer une entreprise par son ID
   */
  async getTenant(id: string): Promise<Tenant> {
    return apiClient.get<Tenant>(`${this.baseUrl}/tenants/${id}`);
  }

  /**
   * Créer une nouvelle entreprise
   */
  async createTenant(data: CreateTenantData): Promise<Tenant> {
    return apiClient.post<Tenant>(`${this.baseUrl}/tenants`, data);
  }

  /**
   * Mettre à jour une entreprise
   */
  async updateTenant(id: string, data: UpdateTenantData): Promise<Tenant> {
    return apiClient.put<Tenant>(`${this.baseUrl}/tenants/${id}`, data);
  }

  /**
   * Supprimer une entreprise
   */
  async deleteTenant(id: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${this.baseUrl}/tenants/${id}`);
  }

  /**
   * Récupérer les statistiques globales
   */
  async getGlobalStats(): Promise<GlobalStats> {
    return apiClient.get<GlobalStats>(`${this.baseUrl}/stats`);
  }

  /**
   * Activer une entreprise
   */
  async activateTenant(id: string): Promise<Tenant> {
    return this.updateTenant(id, { status: 'active' });
  }

  /**
   * Suspendre une entreprise
   */
  async suspendTenant(id: string): Promise<Tenant> {
    return this.updateTenant(id, { status: 'suspended' });
  }

  /**
   * Désactiver une entreprise
   */
  async deactivateTenant(id: string): Promise<Tenant> {
    return this.updateTenant(id, { status: 'inactive' });
  }

  /**
   * Rechercher des entreprises par nom ou email
   */
  async searchTenants(query: string, limit: number = 10): Promise<Tenant[]> {
    const response = await this.getTenants({ 
      search: query, 
      limit,
      page: 1 
    });
    return response.data;
  }

  /**
   * Obtenir les entreprises récemment créées
   */
  async getRecentTenants(limit: number = 5): Promise<Tenant[]> {
    const response = await this.getTenants({ 
      limit,
      page: 1 
    });
    return response.data;
  }

  /**
   * Exporter la liste des entreprises
   */
  async exportTenants(filters: TenantFilters = {}): Promise<Blob> {
    const url = apiClient.buildUrl(`${this.baseUrl}/tenants/export`, filters);
    const response = await apiClient.getAxiosInstance().get(url, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/csv' });
  }

  /**
   * Obtenir l'historique des actions d'une entreprise
   */
  async getTenantHistory(id: string): Promise<any[]> {
    return apiClient.get<any[]>(`${this.baseUrl}/tenants/${id}/history`);
  }

  /**
   * Valider les données d'une entreprise avant création/modification
   */
  validateTenantData(data: Partial<CreateTenantData | UpdateTenantData>): string[] {
    const errors: string[] = [];

    if ('name' in data && data.name && data.name.length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }

    if ('email' in data && data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.push('Format d\'email invalide');
      }
    }

    if ('max_vehicles' in data && data.max_vehicles && data.max_vehicles < 1) {
      errors.push('Le nombre maximum de véhicules doit être d\'au moins 1');
    }

    if ('max_drivers' in data && data.max_drivers && data.max_drivers < 1) {
      errors.push('Le nombre maximum de chauffeurs doit être d\'au moins 1');
    }

    if ('max_missions_per_month' in data && data.max_missions_per_month && data.max_missions_per_month < 1) {
      errors.push('Le nombre maximum de missions par mois doit être d\'au moins 1');
    }

    return errors;
  }

  /**
   * Formater les données d'entreprise pour l'affichage
   */
  formatTenantForDisplay(tenant: Tenant): {
    name: string;
    email: string;
    address: string;
    status: string;
    statusColor: string;
    memberSince: string;
    usage: {
      vehicles: string;
      drivers: string;
      missions: string;
    };
  } {
    const statusMap = {
      active: { text: 'Actif', color: 'green' },
      suspended: { text: 'Suspendu', color: 'orange' },
      inactive: { text: 'Inactif', color: 'red' },
    };

    const status = statusMap[tenant.status] || { text: tenant.status, color: 'default' };

    return {
      name: tenant.name,
      email: tenant.email,
      address: [tenant.address, tenant.city, tenant.postal_code].filter(Boolean).join(', '),
      status: status.text,
      statusColor: status.color,
      memberSince: new Date(tenant.created_at).toLocaleDateString('fr-FR'),
      usage: {
        vehicles: `${tenant.stats.vehicle_count} / ${tenant.max_vehicles}`,
        drivers: `${tenant.stats.driver_count} / ${tenant.max_drivers}`,
        missions: `${tenant.stats.total_missions} (${tenant.stats.completed_missions} terminées)`,
      },
    };
  }

  /**
   * Calculer les métriques d'utilisation d'une entreprise
   */
  calculateUsageMetrics(tenant: Tenant): {
    vehicleUsage: number;
    driverUsage: number;
    completionRate: number;
    isNearLimit: boolean;
  } {
    const vehicleUsage = Math.round((tenant.stats.vehicle_count / tenant.max_vehicles) * 100);
    const driverUsage = Math.round((tenant.stats.driver_count / tenant.max_drivers) * 100);
    const completionRate = tenant.stats.total_missions > 0
      ? Math.round((tenant.stats.completed_missions / tenant.stats.total_missions) * 100)
      : 0;

    const isNearLimit = vehicleUsage > 80 || driverUsage > 80;

    return {
      vehicleUsage,
      driverUsage,
      completionRate,
      isNearLimit,
    };
  }

  /**
   * Obtenir les paramètres de l'entreprise courante
   */
  async getCompanySettings(): Promise<CompanySettings> {
    return apiClient.get<CompanySettings>('/admin/company/settings');
  }

  /**
   * Mettre à jour les paramètres de l'entreprise courante
   */
  async updateCompanySettings(data: UpdateCompanySettingsData): Promise<{
    message: string;
    settings: CompanySettings;
  }> {
    return apiClient.put<{ message: string; settings: CompanySettings }>(
      '/admin/company/settings',
      data
    );
  }
}

// Instance singleton du service
export const tenantService = new TenantService();